/**
 * Organization-Scoped Storage Migration
 *
 * Migrates localStorage keys from vessel-only format to org+vessel format
 *
 * OLD FORMAT: {module}_{vesselId}
 * NEW FORMAT: {module}_{orgId}_{vesselId}
 *
 * Example: crew_VSL001-25 → crew_ORG-abc123_VSL001-25
 */

import { getOrgId } from '../../utils/getOrgId';

/**
 * Patterns that match storage keys needing migration
 * These are the modules using useStorageState with vessel scoping
 */
const STORAGE_KEY_PATTERNS = [
  /^crew_/,
  /^equipment_/,
  /^drill_/,
  /^defect_/,
  /^operations_/,
  /^maintenance_/,
  /^incident_/,
  /^risk_/,
  /^hours_/,
  /^familiarisation_/,
  /^certificate_/,
  /^checklist_/,
  /^bunkering_/,
  /^policy_/,
  /^voyage_/
];

/**
 * Keys to NEVER migrate (these don't follow module_vessel pattern)
 */
const EXCLUDED_KEYS = [
  'sr.feature.orgScopedStorage',
  'sr.impersonation.targetOrgId',
  'sr.impersonation.session',
  'current_user',
  'current_vessel',
  'supabase.auth.token',
  'auth_token',
  'lastSyncTime',
  'appVersion'
];

/**
 * Check if a key should be migrated
 */
function shouldMigrateKey(key) {
  // Skip excluded keys
  if (EXCLUDED_KEYS.includes(key)) {
    return false;
  }

  // Skip keys that already have org format (in case migration runs twice)
  const parts = key.split('_');
  if (parts.length > 2 && parts[1].startsWith('ORG')) {
    return false;
  }

  // Check if key matches any migration pattern
  return STORAGE_KEY_PATTERNS.some(pattern => pattern.test(key));
}

/**
 * Parse old format key to extract module and vesselId
 * Returns: { module, vesselId } or null if invalid
 */
function parseOldKey(key) {
  const parts = key.split('_');
  if (parts.length < 2) {
    return null;
  }

  // Last part should be vessel ID (VSL-xxx or VSLxxx)
  const vesselId = parts[parts.length - 1];
  if (!vesselId.includes('VSL')) {
    return null;
  }

  // Everything before vessel ID is the module name
  const module = parts.slice(0, -1).join('_');

  return { module, vesselId };
}

/**
 * Generate new org-scoped key
 */
function generateNewKey(module, orgId, vesselId) {
  return `${module}_${orgId}_${vesselId}`;
}

/**
 * Scan localStorage and identify keys needing migration
 */
export function scanForMigration() {
  const allKeys = Object.keys(localStorage);
  const keysToMigrate = [];
  const skippedKeys = [];
  const alreadyMigrated = [];

  for (const key of allKeys) {
    if (!shouldMigrateKey(key)) {
      skippedKeys.push(key);
      continue;
    }

    const parsed = parseOldKey(key);
    if (!parsed) {
      skippedKeys.push(key);
      continue;
    }

    // Check if already has org format
    const parts = key.split('_');
    if (parts.length > 2 && parts[parts.length - 2].startsWith('ORG')) {
      alreadyMigrated.push(key);
      continue;
    }

    keysToMigrate.push({
      oldKey: key,
      module: parsed.module,
      vesselId: parsed.vesselId,
      size: localStorage.getItem(key)?.length || 0
    });
  }

  return {
    keysToMigrate,
    skippedKeys,
    alreadyMigrated,
    totalSize: keysToMigrate.reduce((sum, k) => sum + k.size, 0)
  };
}

/**
 * Create backup of all keys before migration
 * Backup keys have prefix: BACKUP_MIGRATION_
 */
export function createBackup(keysToMigrate) {
  const backupKeys = [];

  try {
    for (const item of keysToMigrate) {
      const backupKey = `BACKUP_MIGRATION_${item.oldKey}`;
      const data = localStorage.getItem(item.oldKey);

      if (data) {
        localStorage.setItem(backupKey, data);
        backupKeys.push(backupKey);
      }
    }

    // Store backup metadata
    const backupMetadata = {
      timestamp: new Date().toISOString(),
      keyCount: backupKeys.length,
      keys: backupKeys
    };
    localStorage.setItem('BACKUP_MIGRATION_METADATA', JSON.stringify(backupMetadata));

    return { success: true, backupKeys };
  } catch (error) {
    console.error('[Migration] Backup failed:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Perform the migration
 */
export function performMigration(keysToMigrate, orgId) {
  const migrated = [];
  const failed = [];

  try {
    for (const item of keysToMigrate) {
      try {
        const data = localStorage.getItem(item.oldKey);
        if (!data) {
          failed.push({ ...item, reason: 'No data found' });
          continue;
        }

        // Generate new key
        const newKey = generateNewKey(item.module, orgId, item.vesselId);

        // Write to new key
        localStorage.setItem(newKey, data);

        // Delete old key
        localStorage.removeItem(item.oldKey);

        migrated.push({
          oldKey: item.oldKey,
          newKey: newKey,
          size: data.length
        });
      } catch (error) {
        failed.push({ ...item, reason: error.message });
      }
    }

    // SPECIAL CASE: Migrate default-org keys to correct org
    // This handles data saved by Super Admins before the working org fix
    const defaultOrgPattern = /^(.+)_default-org_(.+)$/;
    const defaultOrgMigrated = [];

    for (const key of Object.keys(localStorage)) {
      const match = key.match(defaultOrgPattern);
      if (match) {
        const [, module, vesselId] = match;

        // Skip if not a known domain module
        if (!STORAGE_KEY_PATTERNS.some(pattern => pattern.test(module))) {
          continue;
        }

        try {
          const data = localStorage.getItem(key);
          if (!data) continue;

          // Use current working org or provided orgId
          const workingOrgId = localStorage.getItem('sr.working.targetOrgId') || orgId;
          const newKey = `${module}_${workingOrgId}_${vesselId}`;

          // Check if target key already exists
          const existing = localStorage.getItem(newKey);
          if (existing) {
            // Merge data (same logic as equipment_register migration)
            try {
              const defaultData = JSON.parse(data);
              const existingData = JSON.parse(existing);

              if (Array.isArray(defaultData) && Array.isArray(existingData)) {
                // Deduplicate by ID
                const mergedMap = new Map();
                existingData.forEach(item => mergedMap.set(item.id, item));
                defaultData.forEach(item => {
                  if (!mergedMap.has(item.id)) {
                    mergedMap.set(item.id, item);
                  }
                });

                const merged = Array.from(mergedMap.values());
                localStorage.setItem(newKey, JSON.stringify(merged));
                console.log(`[Migration] Merged ${key} → ${newKey} (${merged.length} items)`);
              } else {
                // Not an array - keep existing data
                console.log(`[Migration] Skipped ${key} (target exists, not mergeable)`);
              }
            } catch (e) {
              console.error(`[Migration] Failed to merge ${key}:`, e);
            }
          } else {
            // No conflict - direct migration
            localStorage.setItem(newKey, data);
            console.log(`[Migration] Migrated ${key} → ${newKey}`);
          }

          // Remove old key
          localStorage.removeItem(key);

          defaultOrgMigrated.push({
            oldKey: key,
            newKey: newKey,
            module: module,
            vesselId: vesselId
          });
        } catch (error) {
          console.error(`[Migration] Failed to migrate ${key}:`, error);
        }
      }
    }

    console.log(`[Migration] Migrated ${defaultOrgMigrated.length} default-org keys`);

    // Store migration log
    const migrationLog = {
      timestamp: new Date().toISOString(),
      orgId: orgId,
      migrated: migrated,
      failed: failed,
      defaultOrgMigrated: defaultOrgMigrated
    };
    localStorage.setItem('MIGRATION_LOG', JSON.stringify(migrationLog));

    return { success: true, migrated, failed, defaultOrgMigrated };
  } catch (error) {
    console.error('[Migration] Migration failed:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Rollback migration by restoring from backup
 */
export function rollbackMigration() {
  try {
    const metadataStr = localStorage.getItem('BACKUP_MIGRATION_METADATA');
    if (!metadataStr) {
      return { success: false, error: 'No backup found' };
    }

    const metadata = JSON.parse(metadataStr);
    const restored = [];

    // Restore each backed up key
    for (const backupKey of metadata.keys) {
      const originalKey = backupKey.replace('BACKUP_MIGRATION_', '');
      const data = localStorage.getItem(backupKey);

      if (data) {
        localStorage.setItem(originalKey, data);
        localStorage.removeItem(backupKey);
        restored.push(originalKey);
      }
    }

    // Clean up metadata
    localStorage.removeItem('BACKUP_MIGRATION_METADATA');

    // Delete any migrated keys
    const migrationLogStr = localStorage.getItem('MIGRATION_LOG');
    if (migrationLogStr) {
      const log = JSON.parse(migrationLogStr);
      for (const item of log.migrated) {
        localStorage.removeItem(item.newKey);
      }
      localStorage.removeItem('MIGRATION_LOG');
    }

    return { success: true, restored };
  } catch (error) {
    console.error('[Migration] Rollback failed:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Clean up backups after successful migration (after 48h stable period)
 */
export function cleanupBackups() {
  try {
    const metadataStr = localStorage.getItem('BACKUP_MIGRATION_METADATA');
    if (!metadataStr) {
      return { success: true, message: 'No backups to clean' };
    }

    const metadata = JSON.parse(metadataStr);

    // Delete all backup keys
    for (const backupKey of metadata.keys) {
      localStorage.removeItem(backupKey);
    }

    // Delete metadata
    localStorage.removeItem('BACKUP_MIGRATION_METADATA');

    return { success: true, cleaned: metadata.keys.length };
  } catch (error) {
    console.error('[Migration] Cleanup failed:', error);
    return { success: false, error: error.message };
  }
}
